import { EditOutlined } from "@ant-design/icons"
import { Button } from "components/Button/Button"
import { InputInline } from "components/Input/InputInline"
import { InputRTE } from "components/Input/InputRTE"
import { useDisclosure } from "hooks/useDisclosure"
import { Escalation, EscalationActivityEntry } from "interface/escalation"
import { ComponentProps, useRef, useState } from "react"
import { useHotkeys } from "react-hotkeys-hook"
import {
	useAddEscalationComment,
	useDeleteEscalationComment,
	useEditEscalationComment,
} from "services/escalations"
import { useUserRole } from "services/users"
import { cn } from "styles/cn"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { dateFormatter } from "utils/date"

export function EscalationActivityFeed({
	className,
	escalation,
	...props
}: { escalation: Escalation } & ComponentProps<"div">) {
	const { data: { id: userId } = {} } = useUserRole()
	const editButtonRefs = useRef<Record<string, HTMLElement | null>>({})
	const editing = useDisclosure<EscalationActivityEntry["id"]>()

	return (
		<div {...props} className={cn("flex flex-col gap-8", className)}>
			<div
				className={cn(
					"flex flex-col gap-2",
					escalation.activity.length > 0 && "gap-3",
				)}
			>
				<h2 className="text-xl">Responses</h2>

				{escalation.activity.length === 0 && (
					<p className="text-navy-60">Be the first to add a response.</p>
				)}

				{escalation.activity.length > 0 && (
					<ul
						className="flex flex-col divide-y divide-grey-10"
						aria-label={`${escalation.activity.length} response(s)`}
					>
						{escalation.activity.map((entry) => {
							const {
								content = "",
								actorId,
								createdAt,
								editedAt,
								type,
								id,
							} = entry
							const isEditing = editing.isOpen && editing.value === id
							const isEditable = userId === actorId && type === "commented"
							const dateText = dateFormatter(editedAt ?? createdAt, {
								hasYear: true,
							})
							const [actionDescription, action] = getActionText(entry, dateText)

							return (
								<li
									key={id}
									className="flex flex-col gap-2 py-4 whitespace-pre-wrap first:pt-0 last:pb-0"
									aria-label={String(actionDescription)}
								>
									<div className="flex items-center justify-between gap-2">
										<div className="flex flex-wrap gap-2 text-sm">
											<div className="flex flex-wrap gap-1">{action}</div>
											<span className="text-navy-60">{dateText}</span>
											{Boolean(editedAt) && createdAt !== editedAt && (
												<span className="text-navy-60">(edited)</span>
											)}
										</div>
										{isEditable && (
											<Button
												ref={(el) => {
													if (el) {
														editButtonRefs.current[id] = el
													} else {
														delete editButtonRefs.current[id]
													}
												}}
												type="link"
												disabled={editing.isOpen}
												icon={<EditOutlined aria-hidden />}
												aria-label={`Edit comment at ${dateText}`}
												onClick={() => editing.toggle(id)}
											/>
										)}
									</div>

									{type === "commented" && (
										<EditResponse
											readOnly={!isEditing}
											escalationId={escalation.id}
											commentId={id}
											dateText={dateText}
											content={content}
											onClose={(restoreFocus = true) => {
												editing.close()
												if (restoreFocus) {
													requestAnimationFrame(() => {
														editButtonRefs.current[id]?.focus()
													})
												}
											}}
										/>
									)}
								</li>
							)
						})}
					</ul>
				)}
			</div>

			<NewResponse escalationId={escalation.id} />
		</div>
	)
}

function NewResponse({ escalationId }: { escalationId: string }) {
	const containerRef = useRef<HTMLDivElement>(null)
	const add = useAddEscalationComment(escalationId)
	const [value, setValue] = useState("")
	const submitDisabled = value.length === 0 || add.isPending
	const handleSave = () => {
		if (!value) {
			return
		}
		add.mutate(
			{ content: value },
			{
				onSuccess: () => {
					setValue("")
				},
			},
		)
	}

	useHotkeys(
		"mod+enter",
		(e) => {
			if (!containerRef.current?.contains(document.activeElement)) {
				return
			}
			e.preventDefault()
			handleSave()
		},
		{
			enabled: !submitDisabled,
			enableOnFormTags: true,
			enableOnContentEditable: true,
			preventDefault: false,
		},
	)

	return (
		<div ref={containerRef}>
			<div
				className={cn(
					"flex flex-col gap-2.5 rounded-lg border border-grey-20 bg-white p-4 transition-all duration-300 hover:border-green-20 has-focus:border-black",
				)}
			>
				<InputRTE
					value={value}
					disabled={add.isPending}
					status={add.isError ? "error" : undefined}
					placeholder="Leave a response..."
					minRows={1}
					variant="borderless"
					aria-label="Leave a response"
					className="p-0"
					onChange={(nextValue) => {
						add.reset()
						setValue(nextValue)
					}}
				/>
				<Button
					type="primary"
					disabled={submitDisabled}
					className="self-end"
					loading={add.isPending}
					onClick={handleSave}
				>
					Submit
				</Button>
			</div>
			<p
				role={add.isError ? "alert" : undefined}
				className={cn("invisible text-red-40", add.isError && "visible")}
			>
				{add.error?.message}
			</p>
		</div>
	)
}

function EditResponse({
	escalationId,
	commentId,
	dateText,
	content,
	readOnly,
	onClose,
}: {
	escalationId: string
	commentId: string
	dateText: string
	content: string
	readOnly?: boolean
	onClose: (restoreFocus?: boolean) => void
}) {
	const update = useEditEscalationComment(escalationId, commentId)
	const remove = useDeleteEscalationComment(escalationId, commentId)
	const errorMessage = update.error?.message ?? remove.error?.message
	const resetErrors = () => {
		update.reset()
		remove.reset()
	}
	const handleClose = (restoreFocus = true) => {
		resetErrors()
		onClose(restoreFocus)
	}

	return (
		<InputInline
			readOnly={readOnly}
			allowEmpty={false}
			ariaLabel={readOnly ? `Comment at ${dateText}` : "Comment"}
			defaultValue={content}
			errorMessage={errorMessage}
			isPending={update.isPending}
			isRemoving={remove.isPending}
			onChange={resetErrors}
			onCancel={() => handleClose(true)}
			onRemove={() =>
				remove.mutate(undefined, { onSuccess: () => handleClose(false) })
			}
			onSave={(value) => {
				update.mutate(
					{ content: value },
					{ onSuccess: () => handleClose(true) },
				)
			}}
		/>
	)
}

function getActionText(
	{ type, actorName, affectedUsers = [] }: EscalationActivityEntry,
	editAtText: string,
) {
	let action = "Activity by"

	switch (type) {
		case "commented":
			return [`${actorName} ${editAtText}`, <strong>{actorName}</strong>]
		case "created":
			action = "Escalation raised by"
			return [
				`${action} ${actorName} at ${editAtText}`,
				<>
					<span>{action}</span>
					<strong>{actorName}</strong>
				</>,
			]
		case "assigned":
		case "reassigned": {
			action = "assigned escalation to"
			const { fullName = DEFAULT_PLACEHOLDER } = affectedUsers.at(-1) ?? {}
			return [
				`${actorName} ${action} ${fullName} at ${editAtText}`,
				<>
					<strong>{actorName}</strong>
					<span>{action}</span>
					<strong>{fullName}</strong>
				</>,
			]
		}
		case "closed":
			action = "Escalation closed by"
			return [
				`${action} ${actorName} at ${editAtText}`,
				<>
					<span>{action}</span>
					<strong>{actorName}</strong>
				</>,
			]
		case "reopened":
			action = "Escalation re-opened by"
			return [
				`${action} ${actorName} at ${editAtText}`,
				<>
					<span>{action}</span>
					<strong>{actorName}</strong>
				</>,
			]
		case "observers_changed":
			action = "Escalation observers updated by"
			return [
				`${action} ${actorName} at ${editAtText}`,
				<>
					<span>Escalation observers updated by</span>
					<strong>{actorName}</strong>
				</>,
			]
		case "opened_by_assignee":
			action = "Opened by assignee"
			return [
				`${action} ${actorName} at ${editAtText}`,
				<>
					<span>Opened by assignee</span>
					<strong>{actorName}</strong>
				</>,
			]
		default:
			return [
				`${action} ${actorName}`,
				<>
					<span>{action}</span>
					<strong>{actorName}</strong>
				</>,
			]
	}
}
